/* eslint-disable import/namespace */
import { Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';

import { CustomerEditCsvContext } from './CustomerImportCSV';

import { CloseButton } from '@components/ActionButtons';
import {
  FormSectionContainer,
  FormButtonsContainer,
} from '@components/Container';
import {
  ActionableFormikInput,
  CancelButton,
  FormikCheckbox,
  FormikInput,
  SubmitButton,
} from '@components/Form';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import {
  contactDefaultValue,
  ContactValidation,
  ContactViewModel,
} from '@models/Contact';
import { CustomerViewModel } from '@models/Customer';
import { CustomerService } from '@services/Customer';

export const CustomerImportCSVEditContact = () => {
  const { customer, setCustomer, updateCustomerInCsv } = useContext(
    CustomerEditCsvContext
  );
  const [showEditContact, setShowEditContact] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadCustomerBillingAddress = () => {
    CustomerService.getCustomerBillingAddresses(Number(customer?.id)).then(
      (result) => {
        if (result.isSuccess && 'data' in result) {
          setCustomer((prevState) =>
            prevState
              ? {
                  ...prevState,
                  billingAddresses: result.data,
                }
              : prevState
          );
        }
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    loadCustomerBillingAddress();
  }, [customer?.id]);

  const fullName = (contact?: ContactViewModel) => {
    if (!contact) return '';
    return `${contact.firstname || ''} ${contact.lastname || ''}`;
  };

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <ActionableFormikInput
          label="Contact Name"
          name="contactName"
          onEditAction={() => {
            setShowEditContact(true);
          }}
          readonly={true}
        />

        {showEditContact && (
          <Modal show={true}>
            <div className="space-y-4">
              <div className="flex justify-end">
                <CloseButton onClick={() => setShowEditContact(false)} />
              </div>

              <Formik
                initialValues={{
                  ...(customer?.primaryContact ?? contactDefaultValue),
                  contactName: fullName(customer?.primaryContact),
                }}
                validationSchema={ContactValidation}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={(values, actions) => {
                  if (!customer) return;

                  const updatedContact = {
                    ...customer.primaryContact,
                    ...values,
                    id: customer.primaryContact?.id ?? 0,
                  };

                  const updatedCustomer: CustomerViewModel = {
                    ...customer,
                    primaryContact: updatedContact,
                    contacts: customer.contacts?.map((contact) =>
                      contact.id === updatedContact.id
                        ? updatedContact
                        : contact
                    ) ?? [updatedContact],
                  };

                  setCustomer(updatedCustomer);
                  updateCustomerInCsv(updatedCustomer);

                  setShowEditContact(false);
                  actions.setSubmitting(false);
                }}
              >
                {(formikProps) => {
                  useEffect(() => {
                    formikProps.setFieldValue(
                      'contactName',
                      fullName(formikProps.values)
                    );
                  }, [formikProps.values]);

                  return (
                    <>
                      <form method="POST" onSubmit={formikProps.handleSubmit}>
                        <FormSectionContainer>
                          <FormikInput label="First Name" name="firstname" />
                          <FormikInput label="Last Name" name="lastname" />
                          <FormikInput label="Email" name="email" />
                          <FormikInput label="Phone" name="phone" />
                          <FormikInput label="Fax" name="fax" />
                          <FormikCheckbox label="Is Active" name="isActive" />

                          <Space />
                        </FormSectionContainer>
                        <FormButtonsContainer>
                          <CancelButton
                            onClick={() => setShowEditContact(false)}
                          />

                          <SubmitButton
                            label="Save"
                            disabled={
                              formikProps.isSubmitting || !formikProps.isValid
                            }
                          />
                        </FormButtonsContainer>
                      </form>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Modal>
        )}
      </>
    );
  }
};
